import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { DirectUpload } from '@rails/activestorage';
import { Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Checkbox } from 'src/components/Checkbox';
import { Divider } from 'src/components/Divider';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { ImageUploader } from 'src/components/Uploader/ImageUploader';
import { useSession } from 'src/contexts/SessionContext';
import { Leaderboard } from 'src/models/leaderboard/_types';
import { statsForPlatform } from 'src/models/user/utils';
import { FormDatePicker } from 'src/shared/Forms/Fields/FormDatePicker';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { camelize } from 'src/utils/stringFormatter';
import toast from 'src/utils/toast';
import * as Yup from 'yup';

const validationSchema = (t: any) =>
  Yup.object().shape({
    name: Yup.string().required(t('common.validation.required')),
    description: Yup.string().nullable(),
    measurement: Yup.string().required(t('common.validation.required')),
    sortAsc: Yup.boolean(),
    scoresAvailableOn: Yup.date().nullable(),
    imageUrl: Yup.string().nullable(),
    thumbnailUrl: Yup.string().nullable(),
    actionLabel: Yup.string().nullable(),
    actionLink: Yup.string().nullable(),
    displayWithLeaderboardId: Yup.string().nullable(),
    divisions: Yup.array().of(
      Yup.object().shape({
        type: Yup.string().oneOf(['gender', 'age']).required(),
        key: Yup.mixed<string | number>().required(),
        label: Yup.string().required(t('common.validation.required')),
        description: Yup.string().nullable(),
      })
    ),
    filters: Yup.array().of(Yup.string().nullable()),
    newAgeGroup: Yup.string().nullable(),
    hidden: Yup.boolean().nullable(),
    denseRank: Yup.boolean().nullable(),
    ageFilters: Yup.array().of(
      Yup.object().shape({
        label: Yup.string().required(),
        minAge: Yup.number().required(),
        maxAge: Yup.number().required(),
      })
    ),
  });

interface AgeFilter {
  label: string;
  minAge: number;
  maxAge: number;
}

interface LeaderboardFormProps {
  leaderboard?: Partial<Leaderboard>;
  onSubmit: (values: Leaderboard) => Promise<void>;
  otherLeaderboards: Leaderboard[];
}

export const LeaderboardForm = ({
  leaderboard,
  onSubmit,
  otherLeaderboards,
}: LeaderboardFormProps) => {
  const navigate = useNavigate();
  const { session, user } = useSession();
  const { t } = useTranslation('translation', {
    keyPrefix: 'leaderboard.form',
  });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const isNew = !leaderboard?.id;
  const [newMinAge, setNewMinAge] = useState('');
  const [newMaxAge, setNewMaxAge] = useState('');
  const [ageGroupsEnabled, setAgeGroupsEnabled] = useState(false);

  const defaultValues = {
    name: '',
    description: '',
    measurement: '',
    sortAsc: false,
    scoresAvailableOn: null,
    imageUrl: '',
    thumbnailUrl: '',
    actionLabel: '',
    actionLink: '',
    displayWithLeaderboardId: '',
    divisions: [],
    filters: [],
    newAgeGroup: '',
    hidden: false,
    denseRank: false,
    ageFilters: [] as AgeFilter[],
    ...leaderboard,
  };

  // Ensure ageFilters is always an array, never null
  if (!defaultValues.ageFilters) {
    defaultValues.ageFilters = [];
  }

  const handleFileUpload = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, '/api/v4/direct_uploads', {
        directUploadWillCreateBlobWithXHR: (xhr: XMLHttpRequest) => {
          if (user?.authToken) {
            xhr.setRequestHeader('API-TOKEN', user.authToken);
          }
        },
      });

      upload.create((error, blob) => {
        if (error) {
          reject(error);
        } else {
          resolve(blob.signed_id);
        }
      });
    });
  };

  const handleToggleDivision = (
    type: 'gender' | 'age',
    setFieldValue: (field: string, value: any) => void,
    values: any
  ) => {
    const hasDivision = values.divisions.some((d: any) => d.type === type);

    if (hasDivision) {
      setFieldValue(
        'divisions',
        values.divisions.filter((d: any) => d.type !== type)
      );
    } else {
      const newDivisions =
        type === 'gender'
          ? [
              { type: 'gender', key: 'male', label: 'Male', description: '' },
              {
                type: 'gender',
                key: 'female',
                label: 'Female',
                description: '',
              },
            ]
          : [];

      setFieldValue('divisions', [...values.divisions, ...newDivisions]);
    }
  };

  const handleAddAgeGroup = (
    maxAge: string,
    setFieldValue: (field: string, value: any) => void,
    values: any
  ) => {
    const age = parseInt(maxAge, 10);
    if (isNaN(age)) return;

    const divisions = [
      ...values.divisions,
      {
        type: 'age',
        key: age,
        label: `Up to ${age}`,
        description: '',
      },
    ];
    const nonAge = divisions.filter((d) => d.type !== 'age');
    const ages = divisions
      .filter((d) => d.type === 'age')
      .sort((a, b) => Number(a.key) - Number(b.key));

    setFieldValue('divisions', [...nonAge, ...ages]);
  };

  const handleRemoveAgeGroup = (
    key: string | number,
    setFieldValue: (field: string, value: any) => void,
    values: any
  ) => {
    setFieldValue(
      'divisions',
      values.divisions.filter((d: any) => d.key !== key)
    );
  };

  const handleToggleFilter = (
    filter: string,
    setFieldValue: (field: string, value: any) => void,
    values: any
  ) => {
    const hasFilter = values.filters.includes(filter);

    if (hasFilter) {
      setFieldValue(
        'filters',
        values.filters.filter((f: string) => f !== filter)
      );
    } else {
      setFieldValue('filters', [...values.filters, filter]);
    }
  };

  const handleAddAgeFilter = (
    setFieldValue: (field: string, value: any) => void,
    values: any
  ) => {
    const lastFilter =
      values.ageFilters.length > 0
        ? values.ageFilters[values.ageFilters.length - 1]
        : null;

    const minAge = lastFilter ? lastFilter.maxAge + 1 : parseInt(newMinAge, 10);
    const maxAge = parseInt(newMaxAge, 10);

    if (
      isNaN(maxAge) ||
      (lastFilter
        ? maxAge <= lastFilter.maxAge
        : isNaN(minAge) || minAge >= maxAge)
    ) {
      toast.error(t('invalidAgeRange'));
      return;
    }

    const label = `${minAge} - ${maxAge}`;

    setFieldValue('ageFilters', [
      ...values.ageFilters,
      { minAge, maxAge, label },
    ]);
    setNewMinAge('');
    setNewMaxAge('');
  };

  const handleRemoveAgeFilter = (
    index: number,
    setFieldValue: (field: string, value: any) => void,
    values: any
  ) => {
    const newFilters = [...values.ageFilters];
    newFilters.splice(index, 1);
    setFieldValue('ageFilters', newFilters);
  };

  return (
    <Formik
      initialValues={defaultValues}
      validationSchema={validationSchema(tCommon)}
      onSubmit={async (values) => {
        await onSubmit(values as Leaderboard);
        navigate('/leaderboards');
      }}
      enableReinitialize
    >
      {({ handleSubmit, values, setFieldValue }) => {
        const showAgeGroups =
          ageGroupsEnabled || values.divisions.some((d) => d.type === 'age');

        return (
        <Box component="form" onSubmit={handleSubmit} sx={{ pb: 3 }}>
          <Card sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Typography variant="h6">{t('settings')}</Typography>

              <FormTextField
                name="name"
                label={tCommon('labels.name')}
                fullWidth
              />

              <FormTextField
                name="description"
                label={tCommon('labels.description')}
                multiline
                rows={3}
                fullWidth
              />

              <FormTextField
                name="measurement"
                label={t('measurement')}
                fullWidth
                options={
                  session
                    ? statsForPlatform(session).map((stat) => ({
                        label: stat.label,
                        value: stat.label,
                      }))
                    : undefined
                }
              />

              <FormTextField
                name="sortAsc"
                label={t('sorting')}
                fullWidth
                options={[
                  { label: t('sortingOptions.highestWins'), value: 'false' },
                  { label: t('sortingOptions.lowestWins'), value: 'true' },
                ]}
              />

              <FormDatePicker
                name="scoresAvailableOn"
                label={t('scoresAvailableAt')}
              />

              <FormTextField
                name="displayWithLeaderboardId"
                label={t('displayWithLeaderboard')}
                fullWidth
                options={[
                  { label: t('none'), value: '' },
                  ...otherLeaderboards.map((l) => ({
                    label: l.name,
                    value: l.id,
                  })),
                ]}
              />

              <Stack spacing={1}>
                <Checkbox
                  name="hidden"
                  checked={values.hidden}
                  onChange={(e) => setFieldValue('hidden', e.target.checked)}
                  label={t('hidden')}
                />
                <Checkbox
                  name="denseRank"
                  checked={values.denseRank}
                  onChange={(e) => setFieldValue('denseRank', e.target.checked)}
                  label={t('denseRank')}
                />
              </Stack>

              <Divider />

              <Typography variant="h6">{t('images')}</Typography>

              <ImageUploader
                title={t('leaderboardImage')}
                subtitle={t('leaderboardImageSubtitle')}
                onImageSelect={async (file) => {
                  try {
                    const signedId = await handleFileUpload(file);
                    setFieldValue('image', signedId);
                    // Set a preview URL for the UI
                    setFieldValue('imageUrl', URL.createObjectURL(file));
                  } catch (error) {
                    toast.error(
                      tCommon('messages.uploadFailed', { type: 'image' })
                    );
                  }
                }}
                avatarSrc={values.imageUrl}
                isAvatar={false}
                width={500}
                height={50}
              />

              <ImageUploader
                title={t('leaderboardThumbnail')}
                subtitle={t('leaderboardThumbnailSubtitle')}
                onImageSelect={async (file) => {
                  try {
                    const signedId = await handleFileUpload(file);
                    setFieldValue('thumbnail', signedId);
                    // Set a preview URL for the UI
                    setFieldValue('thumbnailUrl', URL.createObjectURL(file));
                  } catch (error) {
                    toast.error(
                      tCommon('messages.uploadFailed', { type: 'thumbnail' })
                    );
                  }
                }}
                avatarSrc={values.thumbnailUrl}
                isAvatar={false}
                width={500}
                height={400}
              />

              <Divider />

              <Typography variant="h6">{t('divisions')}</Typography>
              <Typography variant="body2" color="textSecondary">
                {t('divideParticipants')}
              </Typography>

              <Stack spacing={2}>
                <Checkbox
                  checked={values.divisions.some((d) => d.type === 'gender')}
                  onChange={() =>
                    handleToggleDivision('gender', setFieldValue, values)
                  }
                  label={t('gender')}
                />

                {values.divisions
                  .filter((d) => d.type === 'gender')
                  .map((division, index) => (
                    <Stack key={division.key} spacing={2} sx={{ ml: 3 }}>
                      <Typography>
                        {String(division.key).charAt(0).toUpperCase() +
                          String(division.key).slice(1)}
                      </Typography>
                      <FormTextField
                        name={`divisions.${index}.label`}
                        label={t('divisionName')}
                        fullWidth
                      />
                      <FormTextField
                        name={`divisions.${index}.description`}
                        label={t('divisionDescription')}
                        multiline
                        rows={2}
                        fullWidth
                      />
                      <Divider sx={{ my: 2 }} />
                    </Stack>
                  ))}

                <Checkbox
                  checked={showAgeGroups}
                  onChange={() => {
                    if (showAgeGroups) {
                      setAgeGroupsEnabled(false);
                      setFieldValue(
                        'divisions',
                        values.divisions.filter((d) => d.type !== 'age')
                      );
                    } else {
                      setAgeGroupsEnabled(true);
                    }
                  }}
                  label={t('ageGroups')}
                />

                {values.divisions
                  .filter((d) => d.type === 'age')
                  .map((division, index) => (
                    <Box key={division.key} sx={{ ml: 3 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography>
                          {t('upToAge', { age: division.key })}
                        </Typography>
                        <IconButton
                          onClick={() =>
                            handleRemoveAgeGroup(
                              division.key,
                              setFieldValue,
                              values
                            )
                          }
                          size="small"
                        >
                          <Icon icon={faTrash} />
                        </IconButton>
                      </Stack>
                      <FormTextField
                        name={`divisions.${index}.label`}
                        label={t('ageGroupName')}
                        fullWidth
                      />
                      <FormTextField
                        name={`divisions.${index}.description`}
                        label={t('divisionDescription')}
                        multiline
                        rows={2}
                        fullWidth
                      />
                      <Divider sx={{ my: 2 }} />
                    </Box>
                  ))}

                {showAgeGroups && (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ ml: 3 }}
                  >
                    <Typography>{t('newAgeGroup')}</Typography>
                    <FormTextField
                      name="newAgeGroup"
                      placeholder={t('enterMaxAge')}
                      sx={{ width: 120 }}
                    />
                    <IconButton
                      onClick={() =>
                        handleAddAgeGroup(
                          values.newAgeGroup,
                          setFieldValue,
                          values
                        )
                      }
                    >
                      <Icon icon={faPlus} />
                    </IconButton>
                  </Stack>
                )}
              </Stack>

              <Divider />

              <Typography variant="h6">{t('filters')}</Typography>
              <Typography variant="body2" color="textSecondary">
                {t('allowFilters')}
              </Typography>

              <Stack spacing={2}>
                {['location', 'gender', 'age', 'age_group'].map((filter) => (
                  <Checkbox
                    key={filter}
                    checked={values.filters.includes(filter)}
                    onChange={() =>
                      handleToggleFilter(filter, setFieldValue, values)
                    }
                    label={t(camelize(filter))}
                  />
                ))}
              </Stack>

              <Divider />

              {values.filters.includes('age_group') && (
                <>
                  <Typography variant="h6">{t('ageGroupFilters')}</Typography>

                  <Stack spacing={2}>
                    {values.ageFilters?.map((filter: any, index: number) => (
                      <Box key={index} sx={{ ml: 3 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography>{filter.label}</Typography>
                          <IconButton
                            onClick={() =>
                              handleRemoveAgeFilter(
                                index,
                                setFieldValue,
                                values
                              )
                            }
                            size="small"
                          >
                            <Icon icon={faTrash} />
                          </IconButton>
                        </Stack>
                      </Box>
                    ))}

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ ml: 3 }}
                    >
                      {values.ageFilters.length > 0 ? (
                        <Typography>
                          {values.ageFilters[values.ageFilters.length - 1]
                            .maxAge + 1}{' '}
                          {t('to')}
                        </Typography>
                      ) : (
                        <TextField
                          placeholder={t('enterMinAge')}
                          sx={{ width: 120 }}
                          value={newMinAge}
                          onChange={(e) => setNewMinAge(e.target.value)}
                        />
                      )}
                      <TextField
                        placeholder={t('enterMaxAge')}
                        sx={{ width: 120 }}
                        value={newMaxAge}
                        onChange={(e) => setNewMaxAge(e.target.value)}
                      />
                      <IconButton
                        onClick={() =>
                          handleAddAgeFilter(setFieldValue, values)
                        }
                      >
                        <Icon icon={faPlus} />
                      </IconButton>
                    </Stack>
                  </Stack>
                  <Divider />
                </>
              )}

              <Typography variant="h6">{t('action')}</Typography>

              <FormTextField
                name="actionLabel"
                label={t('actionLabel')}
                fullWidth
              />

              <FormTextField
                name="actionLink"
                label={t('actionLink')}
                fullWidth
              />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="bordered"
                  onClick={() => navigate('/leaderboards')}
                >
                  {tCommon('buttons.cancel')}
                </Button>
                <Button variant="prominent" type="submit">
                  {isNew ? t('createLeaderboard') : t('saveLeaderboard')}
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Box>
        );
      }}
    </Formik>
  );
};
